import * as React from "react";
import classNames from "classnames";
import {twMerge} from "tailwind-merge";
import {OutsideClickHandler} from "./OutsideClickHandler";
import {IconButton} from "../IconButton";
import {imageBaseUrl} from "../Utils/variables";
import {AnimatePresence, motion} from "framer-motion";

export interface DrawerProps
  extends React.ComponentProps<"div">,
    CloseBtnProps {
  active: boolean;
}

export interface CloseBtnProps {
  closeDrawer: () => void;
}

export interface DrawerProfileProps extends React.ComponentProps<"div"> {
  avatarUrl?: string;
  name: string;
}

export interface DrawerBodyProps {
  onItemClick: (id: string) => void;
}

export interface DrawerBodyItemProps extends React.ComponentProps<"div"> {
  itemId: string;
}

const DrawerBodyContext = React.createContext({} as DrawerBodyProps);

const defaultProfileAvatarUrl = `${imageBaseUrl}/img/icons/default-avatar.png`;

export const ProfileDetails = ({
  avatarUrl,
  name,
  className,
  children,
  ...restProps
}: DrawerProfileProps) => {
  return (
    <div
      className={twMerge(
        classNames(
          "flex flex-col items-center justify-center gap-2 px-2 py-4",
          "text-xl font-semibold text-primaryBlue-700",
        ),
        className,
      )}
      {...restProps}
    >
      <img
        className="w-16 bg-white rounded-full drop-shadow"
        src={avatarUrl || defaultProfileAvatarUrl}
        onError={({currentTarget}) => {
          currentTarget.src = defaultProfileAvatarUrl;
        }}
        alt="Profile Image"
      />
      <span
        className="w-full overflow-hidden text-center text-ellipsis"
        title={name}
      >
        {name}
      </span>
      {children}
    </div>
  );
};

const DrawerCloseButton = ({closeDrawer}: CloseBtnProps) => {
  return (
    <IconButton
      id=""
      path={[
        `M2473 3107 c-685 -684 -693 -693 -693 -732 0 -39 8 -48 688 -728 
        378 -378 697 -690 710 -693 42 -11 76 8 158 88 89 86 110 122 100 
        165 -5 19 -191 212 -577 598 l-569 570 565 565 c310 310 570 577 576 
        592 19 47 2 78 -96 176 -147 146 -46 216 -862 -601z`,
      ]}
      className="p-2 text-primary-700 hover:shadow-none"
      color="light"
      onClick={closeDrawer}
    />
  );
};

const DrawerBody = ({
  children,
  onItemClick,
  ...restProps
}: DrawerBodyProps & React.ComponentProps<"div">) => {
  return (
    <DrawerBodyContext.Provider value={{onItemClick}}>
      <div {...restProps}>{children}</div>
    </DrawerBodyContext.Provider>
  );
};

const DrawerBodyItem = ({
  children,
  itemId,
  ...restProps
}: DrawerBodyItemProps) => {
  const {onItemClick} = React.useContext(DrawerBodyContext);

  return (
    <div
      {...restProps}
      onClick={() => onItemClick(itemId)}
      className={twMerge(
        classNames(
          "flex gap-4 items-center px-4 py-2 transition-all duration-100 cursor-pointer",
          "text-primaryBlue-700 bg-white hover:drop-shadow-[0px_0px_2px_lightgrey]",
        ),
        restProps.className,
      )}
    >
      {children}
    </div>
  );
};

export const Drawer = ({
  children,
  active,
  closeDrawer,
  ...restProps
}: DrawerProps) => {
  return (
    <>
      <AnimatePresence>
        {active && (
          <motion.div
            variants={{
              visible: {opacity: 1},
              hidden: {opacity: 0},
            }}
            animate="visible"
            initial="hidden"
            exit="hidden"
            transition={{duration: 0.3}}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md"
          />
        )}
      </AnimatePresence>
      <OutsideClickHandler
        className={classNames(
          "fixed top-0 bottom-0 left-0 w-9/12 bg-white overflow-y-auto overflow-x-hidden",
          "transition-transform duration-300 transform shadow-md sm:w-80 scroll-hidden z-50",
          {"-translate-x-full": !active},
        )}
        {...restProps}
        onOutsideClick={closeDrawer}
      >
        <DrawerCloseButton {...{closeDrawer}} />
        {children}
      </OutsideClickHandler>
    </>
  );
};

Drawer.Body = DrawerBody;
Drawer.BodyItem = DrawerBodyItem;
Drawer.Profile = ProfileDetails;
